import * as React from "react";
import { Rnd } from "react-rnd";
import RndClose, { dragHandleStyles } from "../RndClose/RndClose";
import styled from "styled-components";
import { GrEmoji } from "react-icons/gr";
import { IoMdSend } from "react-icons/io";
import IconPopup from "./IconPopup";
import useClickOutSide from "../../../../hooks/useClickOutSide";
import { Peer } from "peerjs";
import { Input } from "@mui/joy";
import { toast } from "react-toastify";
import { formatTime } from "../../../../utils/formatTime";
import scrollIntoView from "../../../../utils/scrollElm";
import { LoadingButton } from "../../../Loading";
import { BiCopy } from "react-icons/bi";
import "tippy.js/dist/tippy.css";
import { CopyToClipboard } from "react-copy-to-clipboard";
import useDisabled from "../../../../hooks/useDisabled";
import { showPopupType } from "../../Sidebar";
type MessageType = {
  text: string;
  isMe: boolean;
  timestamp: Date;
  nameUser: string;
};
export interface IChatPopup {
  onShow: React.Dispatch<React.SetStateAction<showPopupType>>;
  showChatPopup: boolean;
}

function ChatPopup({ onShow, showChatPopup }: IChatPopup) {
  const {
    menuRef,
    popupRef,
    setShow: setShowIconPopup,
    show: showIconPopup,
  } = useClickOutSide();
  const [myPeerId, setMyPeerId] = React.useState<string>("");
  const [peerIdToConnect, setPeerIdToConnect] = React.useState<string>("");
  const [inputMessage, setInputMessage] = React.useState<string>("");
  const [messages, setMessages] = React.useState<MessageType[]>([]);
  const myPeer = React.useRef<Peer | any>(null);
  const connections = React.useRef<any>([]);
  const [name, setName] = React.useState<string>("");
  const [copied, setCopied] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const messageEndRef = React.useRef<any>(null);
  let lastmessageElm = messageEndRef.current;

  React.useEffect(() => {
    myPeer.current = new Peer();
    myPeer.current.on("open", (peerId: string) => {
      setMyPeerId(peerId);
    });

    myPeer.current.on("connection", (conn: any) => {
      setLoading(false);
      connections.current.push(conn);
      conn.on("data", (data: string) => {
        const newData: MessageType = JSON.parse(data);
        const decodedMessage = decodeURIComponent(newData.text);
        const newMessage: MessageType = {
          text: decodedMessage,
          isMe: false,
          timestamp: new Date(),
          nameUser: newData.nameUser,
        };
        setMessages((prevMessages) => [...prevMessages, newMessage]);
      });
    });

    return () => {
      connections.current.forEach((conn: any) => conn.close());
      myPeer.current.destroy();
    };
  }, []);

  const connectWithOtherPeer = () => {
    if (peerIdToConnect === "" || peerIdToConnect.trim() === "") {
      return;
    }
    const existingConnection = connections.current.find(
      (conn: any) => conn.peer === peerIdToConnect
    );
    if (existingConnection) {
      toast.warn("Current connection!", {
        autoClose: 1500,
        position: "top-center",
      });
      return;
    }
    setLoading(true);
    const connectionData = myPeer.current.connect(peerIdToConnect);
    if (connectionData) {
      connectionData.on("open", () => {
        toast.success("Connected successfully 👌", {
          autoClose: 1500,
          position: "top-center",
        });
        setLoading(false);
        connections.current.push(connectionData);
      });
      connectionData.on("error", (error: any) => {
        console.log(error);
        if (error.type === "peer-unavailable") {
          console.error("Peer with ID", peerIdToConnect, "is not available");
        } else {
          console.error("Connection error:", error);
        }
        toast.error("Connection failed 🙁", {
          autoClose: 1500,
          position: "top-center",
        });
        setLoading(false);
      });

      //nhan data
      connectionData.on("data", (data: string) => {
        const newData: MessageType = JSON.parse(data);
        const decodedMessage = decodeURIComponent(newData.text);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: decodedMessage,
            isMe: false,
            timestamp: new Date(),
            nameUser: newData.nameUser,
          },
        ]);
      });
    } else {
      toast.error("Connection failed 🙁", {
        autoClose: 1500,
        position: "top-center",
      });
      setLoading(false);
    }
  };
  const handleSendMessage = () => {
    if (inputMessage.trim() !== "") {
      connections.current.forEach((conn: any) => {
        const encodedMessage = encodeURIComponent(inputMessage);
        const messageData: MessageType = {
          text: encodedMessage,
          isMe: true,
          timestamp: new Date(),
          nameUser: myPeer?.current?.newName,
        };
        conn.send(JSON.stringify(messageData));
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: inputMessage,
            isMe: true,
            timestamp: new Date(),
            nameUser: myPeer?.current?.newName,
          },
        ]);
      });

      setInputMessage("");
    }
  };
  const handleCreateName = () => {
    if (name.trim() !== "") {
      myPeer.current.newName = name;
      if (myPeer.current.newName) {
        toast.success("Name created successfully 👌", {
          autoClose: 1500,
          position: "top-center",
        });
      } else {
        toast.error("Name creation failed 🙁", {
          autoClose: 1500,
          position: "top-center",
        });
      }
    }
    setName("");
  };
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && inputMessage.trim() !== "") {
        setInputMessage("");
        handleSendMessage();
        setShowIconPopup(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [inputMessage]);
  React.useEffect(() => {
    if (lastmessageElm) {
      scrollIntoView(lastmessageElm, "center");
    }
  }, [lastmessageElm, messages]);
  const { disabledStyle, isDisabled } = useDisabled(loading);
  return (
    <Rnd
      style={{
        zIndex: 310,
      }}
      enableResizing={false}
      dragHandleClassName="dragHandlePdf"
    >
      <StyledChatPopup
        style={{
          display: showChatPopup ? "" : "none",
        }}
        className="absolute pb-10 border border-bordermood flex flex-col popup-menu top-0 -left-[350px] bg-alphaBg px-3 rounded-lg"
      >
        <div
          className={`absolute h-[18px] items-center flex gap-x-1 left-[10px] top-[5px] ${
            myPeer?.current?.newName ? "" : "invisible"
          }`}
        >
          <span className="text-sm">🦁</span>
          <span className="text-white text-xs font-medium">
            {myPeer?.current?.newName}
          </span>
        </div>
        <RndClose
          onClose={() =>
            onShow((prev) => {
              return {
                ...prev,
                showChatPopup: !prev.showChatPopup,
              };
            })
          }
        />
        {myPeer?.current?.newName && (
          <div className="mb-3 flex items-center gap-x-2">
            <div className="my-id text-white text-xs font-light">
              {myPeerId}
            </div>
            <div className="copy-id">
              <CopyToClipboard text={myPeerId} onCopy={() => setCopied(true)}>
                <BiCopy
                  className={` text-sm cursor-pointer ${
                    copied ? "text-primary" : "text-white"
                  }`}
                ></BiCopy>
              </CopyToClipboard>
            </div>
          </div>
        )}
        <div className="flex h-9 gap-x-3">
          {!myPeer?.current?.newName ? (
            <>
              <Input
                className="flex-1  flex-shrink-0"
                color="warning"
                size="sm"
                placeholder="Please enter your name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateName();
                  }
                }}
              />
              <button
                onClick={handleCreateName}
                className="text-white uppercase w-[70px] font-light text-xs px-3 py-1 bg-primary rounded-sm"
              >
                <span className="font-medium">create</span>
              </button>
            </>
          ) : (
            <>
              <Input
                onChange={(e) => setPeerIdToConnect(e.target.value)}
                value={peerIdToConnect}
                className="flex-1  flex-shrink-0"
                color="warning"
                size="sm"
                placeholder="Id your friend"
                variant="outlined"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    connectWithOtherPeer();
                  }
                }}
              />
              <button
                disabled={isDisabled}
                style={disabledStyle}
                onClick={connectWithOtherPeer}
                className="text-white w-[76px] uppercase font-light text-xs px-3 py-1 bg-connectBg rounded-sm"
              >
                {loading ? <LoadingButton /> : <span> connect</span>}
              </button>
            </>
          )}
        </div>
        <div className="w-80">
          <div className="has-scrollbar chat-container mb-3 h-[276px] mt-2 px-2 flex flex-col gap-y-3">
            {messages.length > 0 &&
              messages.map((mess: MessageType, index: number) => {
                return (
                  <div
                    key={index}
                    className={`flex flex-col message-element  max-w-[85%] gap-y-2 ${
                      mess.isMe ? "my-message" : "other-message"
                    }`}
                  >
                    <div className="flex flex-col">
                      <div className="message-block  border-2 px-3 py-2  text-white  rounded-md    leading-[18px] text-sm">
                        {mess.text}
                      </div>
                      <span
                        className={`text-xs peer-name  font-light text-primary ${
                          mess.isMe ? "hidden" : ""
                        }`}
                      >
                        🐹{mess.nameUser}
                      </span>
                    </div>
                    <span className="real-time-message text-white text-xs text-center font-light">
                      {formatTime(mess.timestamp)}
                    </span>
                  </div>
                );
              })}
            <div ref={messageEndRef} className="message-end"></div>
          </div>
          <div className="message-form-container flex gap-x-3 items-center">
            <div className=" relative flex-1  bg-[#121212] rounded-md ">
              <input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Enter text here"
                className="border border-bordermood pl-2 pr-[38px] py-3 rounded-md bg-inherit w-full outline-none text-white"
                type="text"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <div className="right-3 -translate-y-2/4 absolute top-2/4">
                <div
                  onClick={() => setShowIconPopup(!showIconPopup)}
                  ref={menuRef}
                >
                  <GrEmoji className="text-xl transition-all hover:text-primary cursor-pointer z-50  text-white"></GrEmoji>
                </div>
                <IconPopup
                  inputMessage={inputMessage}
                  setInputValue={setInputMessage}
                  ref={popupRef}
                  onShow={setShowIconPopup}
                  show={showIconPopup}
                />
              </div>
            </div>
            <div className="btn-send-message" onClick={handleSendMessage}>
              <IoMdSend className="text-white text-lg hover:text-primary transition-all cursor-pointer"></IoMdSend>
            </div>
          </div>
        </div>
      </StyledChatPopup>
    </Rnd>
  );
}
export default React.memo(ChatPopup);
const StyledChatPopup = styled.div`
  .MuiInput-sizeSm {
    border-radius: 4px;
    background-color: hsla(0, 0%, 100%, 0.05);
    color: #fff;
    flex: 1;
    flex-shrink: 0;
    width: 238px;
  }
  .my-message {
    margin-left: auto;
    & .message-block {
      border: 1px solid hsla(0, 0%, 100%, 0.05);
      background-color: #f3a952;
      border-bottom-right-radius: 0px;
    }
  }
  .other-message {
    margin-right: auto;
    & .message-block {
      border: 1px solid hsla(0, 0%, 100%, 0.05);
      border-bottom-left-radius: 0px;
      background-color: initial;
    }
  }
`;
